import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw } from "lucide-react";
import {
  outcomeLabel,
  actorScopeLabel,
  errorCategoryLabel,
} from "@/components/master/_labels";

/**
 * Phase 36 — Password-reset attempts diagnostic table.
 *
 * SAFE-FIELD SURFACE ONLY. The backend
 * (`routers/master_tenant_detail.py:_safe_attempt`) strips
 * `requested_identifier_hash` from the response. Raw emails are never
 * stored in the collection to begin with.
 *
 * Raw enum values are preserved via `data-outcome`, `data-actor-scope`,
 * and `data-error-category` attributes so CSS/Playwright can select on
 * them; visible text uses the friendly labels from `_labels.js`.
 */
const OUTCOME_BADGE = {
  provider_accepted: "bg-secondary/15 text-secondary",
  provider_failed: "bg-destructive/10 text-destructive",
  native_login_disabled: "bg-muted text-foreground",
  disabled_account: "bg-muted text-foreground",
  duplicate_identity_rejected: "bg-destructive/10 text-destructive",
  rate_limited: "bg-muted text-foreground",
  account_not_found: "bg-muted text-foreground",
  identity_integrity_error: "bg-destructive/10 text-destructive",
  action_needed: "bg-destructive/10 text-destructive",
};

const ACTOR_BADGE = {
  public: "bg-muted text-foreground",
  master_triggered: "bg-primary/10 text-primary",
};

export default function ResetAttemptsTable({ tenantId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/master/tenants/${tenantId}/password-reset-attempts`, {
        params: { limit: 50 },
      });
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId]);

  return (
    <div className="space-y-3" data-testid="reset-attempts-table">
      <div className="flex items-center justify-end">
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="reset-attempts-refresh">
          {loading ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-2 h-3.5 w-3.5" />}
          Refresh
        </Button>
      </div>
      <div className="overflow-hidden rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Timestamp</TableHead>
              <TableHead>Outcome</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Provider id</TableHead>
              <TableHead>Error</TableHead>
              <TableHead>Slug</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                  {loading ? "Loading…" : "No reset attempts recorded for this workspace yet."}
                </TableCell>
              </TableRow>
            )}
            {items.map((a) => (
              <TableRow key={a.id} data-testid={`attempt-row-${a.id}`}>
                <TableCell className="text-xs font-mono">{fmtTs(a.timestamp)}</TableCell>
                <TableCell>
                  <Badge
                    className={OUTCOME_BADGE[a.outcome] || "bg-muted"}
                    data-testid={`attempt-outcome-${a.id}`}
                    data-outcome={a.outcome}
                  >
                    {outcomeLabel(a.outcome)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge
                    className={ACTOR_BADGE[a.actor_scope] || "bg-muted"}
                    data-actor-scope={a.actor_scope}
                  >
                    {actorScopeLabel(a.actor_scope)}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono text-[11px]">{a.provider_message_id || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground" data-error-category={a.error_category || ""}>
                  {errorCategoryLabel(a.error_category)}
                </TableCell>
                <TableCell className="font-mono text-[11px]">{a.requested_tenant_slug || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function fmtTs(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(); } catch (_e) { return String(v); }
}
